import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useViewAsClient } from "@/lib/viewAsClient";
import {
  Card,
  CardContent,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Ban,
  MoreHorizontal,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import {
  deleteBlackout,
  listBlackouts,
  listSitesLite,
} from "@/lib/api";
import { BLACKOUT_ROLE_STYLE } from "@/lib/constants";
import BlackoutForm from "@/components/BlackoutForm";

export default function Blackouts() {
  const { viewAsClientId } = useViewAsClient();
  const [rows, setRows] = useState([]);
  const [sites, setSites] = useState([]);
  const [siteFilter, setSiteFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const [tick, setTick] = useState(0);
  const refresh = () => setTick((k) => k + 1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await listSitesLite();
      if (cancelled) return;
      setSites(data || []);
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data, error } = await listBlackouts({
        siteId: siteFilter === "all" ? null : siteFilter,
        clientId: viewAsClientId || null,
      });
      if (cancelled) return;
      if (error) toast.error(error.message);
      setRows(data || []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [siteFilter, viewAsClientId, tick]);

  const doDelete = async () => {
    if (!confirmDelete) return;
    const { error } = await deleteBlackout(confirmDelete.id);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Blackout deleted.");
    setConfirmDelete(null);
    refresh();
  };

  return (
    <div data-testid="blackouts-page" className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Ban className="h-6 w-6 text-slate-700" /> Blackouts
          </h1>
          <p className="text-sm text-slate-500">
            Close a bay (or the whole site) for a period. Shading colour on the dock grid
            reflects who created the blackout.
          </p>
        </div>
        <Button onClick={() => setEditing({ __new: true })} data-testid="blackout-new-btn">
          <Plus className="h-4 w-4 mr-1.5" /> New blackout
        </Button>
      </div>

      <Card>
        <CardContent className="py-3 flex flex-wrap items-center gap-3">
          <span className="text-xs text-slate-500">Site</span>
          <Select value={siteFilter} onValueChange={setSiteFilter}>
            <SelectTrigger className="w-60" data-testid="blackout-site-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sites</SelectItem>
              {sites.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name} {s.code ? `· ${s.code}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex flex-wrap items-center gap-2 ml-auto text-xs text-slate-500">
            <span className="font-medium">Legend:</span>
            {Object.entries(BLACKOUT_ROLE_STYLE)
              .filter(([k]) => k !== "default")
              .map(([role, style]) => (
                <span key={role} className="inline-flex items-center gap-1">
                  <span
                    className="inline-block h-3 w-4 rounded ring-1"
                    style={{ backgroundColor: style.bg, boxShadow: `inset 0 0 0 1px ${style.ring}` }}
                  />
                  {role.replace("_", " ")}
                </span>
              ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Site / bay</TableHead>
              <TableHead>Start</TableHead>
              <TableHead>End</TableHead>
              <TableHead>Created by</TableHead>
              <TableHead className="w-12" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={6} className="py-12 text-center text-sm text-slate-400">
                  No blackouts.
                  <Button
                    variant="link"
                    onClick={() => setEditing({ __new: true })}
                    data-testid="blackout-empty-cta"
                  >
                    Add the first one
                  </Button>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => {
                const style = BLACKOUT_ROLE_STYLE[r.created_by_role] || BLACKOUT_ROLE_STYLE.default;
                return (
                  <TableRow key={r.id} data-testid={`blackout-row-${r.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span
                          className="inline-block h-3 w-3 rounded"
                          style={{ backgroundColor: style.bg }}
                          title={style.label}
                        />
                        <span className="font-medium">{r.title}</span>
                      </div>
                      {r.reason && (
                        <div className="text-xs text-slate-500 mt-0.5 line-clamp-1">{r.reason}</div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">{r.site?.name || "—"}</div>
                      <div className="text-xs text-slate-500">
                        {r.bay?.name ? (
                          <span className="inline-flex items-center gap-1">
                            <span
                              className="inline-block h-2 w-2 rounded-full"
                              style={{ backgroundColor: r.bay.color || "#94a3b8" }}
                            />
                            {r.bay.name}
                          </span>
                        ) : (
                          <span className="italic">Whole site</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs tabular-nums">{fmt(r.start_time, r.site?.timezone)}</TableCell>
                    <TableCell className="text-xs tabular-nums">{fmt(r.end_time, r.site?.timezone)}</TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="text-[10px]">
                        {r.created_by_role?.replace("_", " ") || "—"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`blackout-actions-${r.id}`}>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setEditing(r)} data-testid={`blackout-edit-${r.id}`}>
                            <Pencil className="mr-2 h-4 w-4" /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-rose-600 focus:text-rose-700"
                            onClick={() => setConfirmDelete(r)}
                            data-testid={`blackout-delete-${r.id}`}
                          >
                            <Trash2 className="mr-2 h-4 w-4" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>

      <BlackoutForm
        open={!!editing}
        onOpenChange={(o) => !o && setEditing(null)}
        blackout={editing?.__new ? null : editing}
        defaultSiteId={editing?.__new && siteFilter !== "all" ? siteFilter : null}
        onSaved={refresh}
      />

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this blackout?</AlertDialogTitle>
            <AlertDialogDescription>
              The closure will be removed from the grid immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="blackout-delete-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function fmt(iso, tz) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-AU", {
    timeZone: tz || "Australia/Sydney",
    weekday: "short",
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}
